import {
  format,
  isSameDay,
  parse,
  roundToNearestMinutes,
  setMinutes,
} from "date-fns"
import PropTypes from "prop-types"
import React, { useState } from "react"

import Button from "src/components/Button"
import Form from "src/components/Form"
import Modal from "src/components/Modal"

import { scheduleWorkOrder } from "src/api/Service/WorkOrders"

import { formatDateOnly, parseDateOnly } from "src/utils/date_only"

import { SCHEDULE_WORK_ORDER_PREFIX } from "./amplitude_events"
import useWorkOrderActionMutation from "./hooks/use_work_order_action_mutation"

const ScheduleWorkOrderModal = ({
  isOpen,
  onClose,
  workOrder,
  workOrderSequentialId,
}) => {
  const isReschedule = Boolean(workOrder.scheduledAt)
  const [date, setDate] = useState(parseDateOnly(workOrder.scheduledAt))
  const [time, setTime] = useState(
    workOrder.scheduledTime
      ? parse(workOrder.scheduledTime, "HH:mm", new Date())
      : null
  )

  // Date and time are marina wall-clock values; the server places them in
  // the marina's timezone, so they are sent as plain strings, never instants.
  const { mutate, isLoading, handleCancel } = useWorkOrderActionMutation({
    workOrderSequentialId,
    action: (marinaSlug, sequentialId) =>
      scheduleWorkOrder(marinaSlug, sequentialId, {
        scheduledAt: formatDateOnly(date),
        scheduledTime: time ? format(time, "HH:mm") : null,
      }),
    eventPrefix: SCHEDULE_WORK_ORDER_PREFIX,
    successMessage: isReschedule
      ? "Work order rescheduled"
      : "Work order scheduled",
    errorMessage: "Failed to schedule work order.",
    onClose,
  })

  // The input accepts typed text, so keep a typed time on the 15-minute steps
  // the server requires, without rounding past midnight onto the next day.
  const handleTimeChange = (value) => {
    if (!value) return setTime(value)

    const rounded = roundToNearestMinutes(value, { nearestTo: 15 })
    setTime(isSameDay(rounded, value) ? rounded : setMinutes(value, 45))
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      dismissable={!isLoading}
      size="default"
    >
      <Modal.Header>
        <h4 className="m-0 text-xl font-bold">
          {isReschedule ? "Reschedule Work Order" : "Schedule Work Order"}
        </h4>
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <div>
            <Form.Label htmlFor="schedule-work-order-date" required>
              Date
            </Form.Label>
            <Form.DatePicker
              id="schedule-work-order-date"
              value={date}
              onChange={setDate}
              minDate={parseDateOnly(workOrder.marinaToday) ?? new Date()}
            />
          </div>
          <div>
            <Form.Label htmlFor="schedule-work-order-time" optional>
              Start time
            </Form.Label>
            <div className="flex items-center gap-2">
              <Form.TimePicker
                id="schedule-work-order-time"
                value={time}
                onChange={handleTimeChange}
                timeIntervals={15}
              />
              {time && (
                <Button
                  variant="link"
                  type="button"
                  onClick={() => setTime(null)}
                >
                  Clear time
                </Button>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            type="button"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={() => mutate()}
            disabled={!date}
            isLoading={isLoading}
          >
            Save
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

ScheduleWorkOrderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  workOrder: PropTypes.shape({
    marinaToday: PropTypes.string,
    scheduledAt: PropTypes.string,
    scheduledTime: PropTypes.string,
  }).isRequired,
  workOrderSequentialId: PropTypes.string.isRequired,
}

export default ScheduleWorkOrderModal
